/**
 * Phase D D.11.2 — Textarea avec bouton "Ameliorer avec IA" (reformulation Claude Sonnet).
 *
 * Endpoint : POST /chantiers/{cid}/crc/{crc_id}/reformuler
 * Section types : "problematique", "espace_libre", "synthese_semaine", "constat"
 */
import { useState } from 'react';
import axios from 'axios';
import { Sparkle, ArrowsClockwise, ArrowUUpLeft } from '@phosphor-icons/react';
import useIA from '../../hooks/useIA';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ReformulableTextarea({
  value,
  onChange,
  sectionType,
  contexte = '',
  rows = 4,
  placeholder = '',
  chantierId,
  crcId,
  label,
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [textBefore, setTextBefore] = useState(null);
  // RP96 — réglage IA du cabinet : bouton grisé avec explication si non disponible
  const ia = useIA();
  const iaOff = !ia.disponible('compte_rendu');

  const reformuler = async () => {
    if (!value || !value.trim()) {
      setError('Texte vide');
      return;
    }
    setLoading(true);
    setError(null);
    setTextBefore(value);
    try {
      const res = await axios.post(
        `${API_URL}/api/chantiers/${chantierId}/crc/${crcId}/reformuler`,
        { texte_brut: value, section_type: sectionType, contexte },
        { withCredentials: true },
      );
      onChange(res.data.texte_reformule || value);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Erreur reformulation IA';
      setError(detail);
    } finally {
      setLoading(false);
    }
  };

  const undoReformulation = () => {
    if (textBefore !== null) {
      onChange(textBefore);
      setTextBefore(null);
    }
  };

  return (
    <div className="space-y-2">
      {label && (
        <label className="block text-sm font-medium text-gray-700">{label}</label>
      )}
      <textarea
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        placeholder={placeholder}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
      />
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={reformuler}
            disabled={loading || !value?.trim() || iaOff}
            title={ia.raison('compte_rendu')}
            className="px-3 py-1.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-lg flex items-center gap-1.5 hover:bg-blue-100 disabled:opacity-50 text-sm"
            data-testid="reformulable-btn-improve"
          >
            {loading ? (
              <ArrowsClockwise size={16} className="animate-spin" />
            ) : (
              <Sparkle size={16} />
            )}
            {loading ? 'Reformulation…' : 'Améliorer avec IA'}
          </button>
          {textBefore !== null && (
            <button
              type="button"
              onClick={undoReformulation}
              className="px-3 py-1.5 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm flex items-center gap-1"
              data-testid="reformulable-btn-undo"
            >
              <ArrowUUpLeft size={14} /> Annuler
            </button>
          )}
        </div>
        {error && <span className="text-sm text-red-600">{error}</span>}
        {iaOff && !error && <span className="text-xs text-gray-500" data-testid="reformulable-ia-off">{ia.raison('compte_rendu')}</span>}
      </div>
    </div>
  );
}
